import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2 } from "lucide-react";
import { API, LOCAL_APP_VERSION } from "@/config/constants";

const SEEN_KEY = "yems_whatsnew_seen_version";

/**
 * What's New dialog — shows release-note highlights for the current build.
 *
 * Auto-opens once per version when the user lands on a newer build (decided
 * by comparing LOCAL_APP_VERSION against the value stored in localStorage).
 * Can also be opened manually via the sidebar button.
 *
 * Props:
 *   open / onOpenChange — when provided, the parent fully controls the dialog
 *                         (i.e. clicking the sidebar button). When omitted,
 *                         the dialog runs in auto-show mode.
 */
export default function WhatsNewDialog({ open, onOpenChange }) {
  const [autoOpen, setAutoOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  const isControlled = typeof open === "boolean";
  const isOpen = isControlled ? open : autoOpen;

  // Auto-show once per version on cold launch
  useEffect(() => {
    if (isControlled) return;
    try {
      const seen = localStorage.getItem(SEEN_KEY);
      if (seen !== LOCAL_APP_VERSION) {
        // Tiny delay so we don't fight the login/onboarding flow
        const t = setTimeout(() => setAutoOpen(true), 1500);
        return () => clearTimeout(t);
      }
    } catch {
      // ignore — never block the app for this dialog
    }
  }, [isControlled]);

  const loadEntries = useCallback(async () => {
    if (entries.length > 0) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/changelog`, { params: { _t: Date.now() } });
      setEntries(res.data?.entries || []);
    } catch {
      // Offline / failed — leave empty; UI will say no notes available
    } finally {
      setLoading(false);
    }
  }, [entries.length]);

  useEffect(() => {
    if (isOpen) loadEntries();
  }, [isOpen, loadEntries]);

  const handleOpenChange = (next) => {
    if (!next) {
      // Mark current version as seen so we don't auto-open again
      try { localStorage.setItem(SEEN_KEY, LOCAL_APP_VERSION); } catch {}
    }
    if (isControlled) onOpenChange?.(next);
    else setAutoOpen(next);
  };

  const current = entries[0];

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Sparkles className="w-5 h-5 text-cyan-400" />
            What's New
            <Badge variant="outline" className="text-[10px] font-mono ml-1">v{LOCAL_APP_VERSION}</Badge>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Release highlights for the EMS Protocol App.
          </DialogDescription>
        </DialogHeader>

        {loading && entries.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading release notes…
          </div>
        ) : entries.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground" data-testid="whats-new-empty">
            Release notes are unavailable while offline.
          </div>
        ) : (
          <div className="space-y-5" data-testid="whats-new-entries">
            {/* Current/highlighted release */}
            {current && (
              <div
                className="rounded-lg border-2 border-cyan-500/40 bg-cyan-500/5 p-4 space-y-3"
                data-testid={`whats-new-${current.version}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-cyan-400 font-semibold">Latest release</div>
                    <div className="font-heading font-bold text-base mt-0.5">{current.title}</div>
                  </div>
                  <Badge className="bg-cyan-600 text-white text-[10px] font-mono shrink-0">
                    v{current.version}
                  </Badge>
                </div>
                <ul className="space-y-2">
                  {current.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm">
                      <span className="text-base shrink-0 leading-tight">{h.icon}</span>
                      <span className="text-foreground/90 leading-snug">{h.text}</span>
                    </li>
                  ))}
                </ul>
                <div className="text-[11px] text-muted-foreground pt-1 border-t border-cyan-500/20">
                  Released {current.date}
                </div>
              </div>
            )}

            {/* Past releases */}
            {entries.length > 1 && (
              <div className="space-y-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold pl-1">
                  Earlier releases
                </div>
                {entries.slice(1).map((e) => (
                  <details
                    key={e.version}
                    className="group rounded-lg border border-border bg-secondary/10 overflow-hidden"
                    data-testid={`whats-new-${e.version}`}
                  >
                    <summary className="px-3 py-2.5 cursor-pointer flex items-center justify-between gap-2 list-none">
                      <span className="flex items-center gap-2 min-w-0">
                        <Badge variant="outline" className="text-[10px] font-mono shrink-0">v{e.version}</Badge>
                        <span className="text-sm font-semibold truncate">{e.title}</span>
                      </span>
                      <span className="text-[10px] text-muted-foreground shrink-0">{e.date}</span>
                    </summary>
                    <ul className="px-3 pb-3 pt-1 space-y-1.5 border-t border-border bg-background/40">
                      {e.highlights.map((h, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs">
                          <span className="shrink-0">{h.icon}</span>
                          <span className="text-muted-foreground leading-snug">{h.text}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                ))}
              </div>
            )}
          </div>
        )}

        <Button
          className="w-full mt-2"
          onClick={() => handleOpenChange(false)}
          data-testid="whats-new-close"
        >
          Got it
        </Button>
      </DialogContent>
    </Dialog>
  );
}
